import { action, makeObservable, observable } from "mobx";

export type IModalStoreInitData = {
  currentOrganization: SoprisAppsProps["pageProps"]["currentOrganization"];
  user?: SoprisAppsProps["pageProps"]["user"];
  featureFlags?: { posthog_tracking_enabled?: { enabled: boolean } };
};

export type IModal = {
  type: string;
  posthog_params?: Record<string, unknown>;
  [key: string]: unknown;
};

// Staff modal opens are always sent. Visitor opens are sampled to limit
// PostHog volume. Multiply visitor counts by 1 / sample_rate.
const VISITOR_SAMPLE_RATE = 0.1;

export default class ModalStore {
  initialized = false;
  posthogTrackingEnabled = false;
  isStaff = false;
  userId: IUser["id"] | null = null;
  currentOrgId: OrganizationTypeOrganization["id"] | undefined;

  constructor(initData: IModalStoreInitData | undefined) {
    this.initialize(initData);

    makeObservable(this, {
      stack: observable,
      addModal: action,
      clearAllModals: action,
      removeTop: action,
    });
  }

  initialize = (initData: IModalStoreInitData | undefined) => {
    if (initData) {
      this.currentOrgId = initData.currentOrganization.id;
      this.posthogTrackingEnabled =
        initData.featureFlags?.posthog_tracking_enabled?.enabled ?? false;
      this.userId = initData.user?.id ?? null;
      const permissions = initData.currentOrganization.user_permissions;
      this.isStaff = Boolean(permissions?.admin || permissions?.editor);
      this.initialized = true;
    }
  };

  serialize = () => {
    return {
      currentOrganization: {
        id: this.currentOrgId,
      },
      featureFlags: {
        posthog_tracking_enabled: { enabled: this.posthogTrackingEnabled },
      },
      currentUser: {
        id: this.userId,
      },
    };
  };

  stack: IModal[] = [];

  removeTop = () => {
    this.stack.pop();
  };

  _trackModalEvent = (modalObj: IModal) => {
    if (!this.posthogTrackingEnabled || typeof window === "undefined") {
      return;
    }

    const sampleRate = this.isStaff ? 1 : VISITOR_SAMPLE_RATE;
    if (Math.random() >= sampleRate) {
      return;
    }

    const properties = {
      modal_type: modalObj.type,
      organization_id: this.currentOrgId,
      user_id: this.userId,
      ...modalObj.posthog_params,
      sample_rate: sampleRate,
    };

    import("posthog-js").then(({ default: posthog }) => {
      posthog.capture("modal_opened", properties);
    });
  };

  addModal = (modalObj: IModal) => {
    this.stack.push(modalObj);
    this._trackModalEvent(modalObj);
  };

  clearAllModals = () => {
    this.stack.length = 0;
  };
}
